import type { TribeId, DemographicKey, Question, ResponseDistribution, FilterKey } from '@/types'

/**
 * Build a standardized filter key string for data lookup.
 *
 * @param tribe - Tribe identifier (usually 'total')
 * @param breakType - Break type (e.g., 'age', 'gender')
 * @param breakValue - Specific break value (e.g., '18-34')
 * @param province - Optional province filter for cross-filtering
 */
export function buildFilterKey(
  tribe: TribeId,
  breakType: DemographicKey,
  breakValue?: string,
  province?: string
): FilterKey {
  let key = `tribe:${tribe}|break:${breakType}`
  if (breakValue && breakType !== 'total') {
    key = `${key}:${breakValue}`
  }
  if (province) {
    key = `${key}|province:${province}`
  }
  return key as FilterKey
}

/**
 * Parse a filter key back into its components.
 */
export function parseFilterKey(key: FilterKey): {
  tribe: TribeId
  breakType: DemographicKey
  breakValue?: string
} {
  const [tribePart, breakPart] = key.split('|')
  const tribe = tribePart.replace('tribe:', '') as TribeId
  const breakComponents = breakPart.replace('break:', '').split(':')
  const breakType = breakComponents[0] as DemographicKey
  const breakValue = breakComponents[1]
  return { tribe, breakType, breakValue }
}

/**
 * Get response distribution for a question with current filters.
 */
export function getFilteredData(
  question: Question,
  tribe: TribeId,
  breakType: DemographicKey,
  breakValue?: string
): ResponseDistribution | null {
  const filterKey = buildFilterKey(tribe, breakType, breakValue)

  if (question.data[filterKey]) {
    return question.data[filterKey]
  }

  // Fall back to tribe + total break if specific break not found
  if (breakType !== 'total') {
    const fallbackKey = buildFilterKey(tribe, 'total')
    if (question.data[fallbackKey]) {
      return question.data[fallbackKey]
    }
  }

  // Fall back to total + total if nothing else found
  const totalKey = buildFilterKey('total', 'total')
  return question.data[totalKey] || null
}

/**
 * Get all available filter keys for a question.
 */
export function getAvailableFilters(question: Question): FilterKey[] {
  return Object.keys(question.data) as FilterKey[]
}

/**
 * Check if a question has data for a specific filter combination.
 */
export function hasFilterData(
  question: Question,
  tribe: TribeId,
  breakType: DemographicKey,
  breakValue?: string
): boolean {
  const filterKey = buildFilterKey(tribe, breakType, breakValue)
  return filterKey in question.data
}

/**
 * Get sample size for current filter combination.
 */
export function getSampleSize(
  question: Question,
  tribe: TribeId,
  breakType: DemographicKey,
  breakValue?: string
): number {
  const data = getFilteredData(question, tribe, breakType, breakValue)
  return data?.sampleSize ?? 0
}

/**
 * Check if sample size is below warning threshold.
 */
export function isSampleSizeWarning(sampleSize: number, threshold = 30): boolean {
  return sampleSize > 0 && sampleSize < threshold
}
